import type { TokenMappings } from '../types/configuration.types';

/**
 * Returns the appropriate status badge class based on a color hint.
 * Consumes dynamic mappings from the theme configuration if provided.
 */
export const getStatusColorClasses = (colorHint?: string, mappings?: Record<string, string>): string => {
  if (colorHint && mappings && mappings[colorHint]) {
    return mappings[colorHint];
  }

  // Fallback to optimized legacy logic if mappings are missing or key not found
  switch (colorHint) {
    case 'green':
    case 'success':
      return 'status-badge-success';
    case 'yellow':
    case 'warning':
      return 'status-badge-warning';
    case 'red':
    case 'danger':
    case 'error':
      return 'status-badge-error';
    case 'blue':
    case 'info':
      return 'status-badge-info';
    case 'orange':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900/50 dark:text-orange-300';
    case 'purple':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300';
    case 'neutral':
    case 'gray':
      return 'status-badge-neutral';
    default:
      return 'bg-theme-secondary/10 text-theme-secondary dark:bg-theme-secondary/20 transition-colors';
  }
};

/**
 * Maps common Tailwind-like tokens or multi-value strings to valid CSS property values.
 * Uses dynamic mappings from the backend-driven configuration registry.
 */
export const mapTokenToCss = (value: string | undefined, mappings?: TokenMappings): string => {
  if (!value) return '';

  // 1. Padding/Spacing Mapping (e.g. "px-6 py-4")
  if (value.includes('px-') || value.includes('py-')) {
    const parts = value.split(' ');
    const mappedParts = parts.map(part => {
      // Priority 1: Dynamic Registry
      if (mappings?.padding && mappings.padding[part]) {
        return mappings.padding[part];
      }

      // Priority 2: Standard Tailwind Fallback (4px baseline)
      const numMatch = part.match(/-(?:\d*\.)?\d+$/);
      if (numMatch) {
        const numValue = parseFloat(numMatch[0].substring(1));
        return `${numValue * 0.25}rem`;
      }

      // Priority 3: Common Literal Fallbacks
      const staticMapping: Record<string, string> = {
        'px-4': '1rem', 'px-6': '1.5rem', 'py-2': '0.5rem', 'py-4': '1rem'
      };
      return staticMapping[part] || part;
    });

    const x = mappedParts.find((_, i) => parts[i].startsWith('px-')) || '0';
    const y = mappedParts.find((_, i) => parts[i].startsWith('py-')) || '0';

    // Ensure we don't return raw tokens like "px-8" in the final CSS string
    const finalX = x.includes('rem') || x.includes('px') || x === '0' ? x : '0';
    const finalY = y.includes('rem') || y.includes('px') || y === '0' ? y : '0';

    return `${finalY} ${finalX}`;
  }

  // 2. Font Size Mapping (e.g. "text-xs")
  if (mappings?.fontSize && mappings.fontSize[value]) return mappings.fontSize[value];
  // Standard Tailwind fallbacks
  const fontSizeFallback: Record<string, string> = {
    'text-xs': '0.75rem', 'text-sm': '0.875rem', 'text-base': '1rem', 'text-lg': '1.125rem'
  };
  if (fontSizeFallback[value]) return fontSizeFallback[value];

  // 3. Font Weight Mapping (e.g. "font-semibold")
  if (mappings?.fontWeight && mappings.fontWeight[value]) return mappings.fontWeight[value];
  const fontWeightFallback: Record<string, string> = {
    'font-normal': '400', 'font-medium': '500', 'font-semibold': '600', 'font-bold': '700'
  };
  if (fontWeightFallback[value]) return fontWeightFallback[value];

  // 4. Letter Spacing Mapping (e.g. "tracking-wider")
  if (mappings?.letterSpacing && mappings.letterSpacing[value]) return mappings.letterSpacing[value];

  // 5. Vertical Align Mapping (e.g. "align-middle")
  if (value.startsWith('align-')) return value.replace('align-', '');

  return value;
};

/**
 * Returns CSS color class mappings for various student promotion action outcomes.
 */
export const getPromotionActionBadgeClasses = (action: string): string => {
  switch (action) {
    case 'PROMOTED':
      return 'bg-success/10 text-success border border-success/20';
    case 'HELD_BACK':
      return 'bg-danger/10 text-danger border border-danger/20';
    case 'DEFERRED':
      return 'bg-warning/10 text-warning border border-warning/20';
    case 'PROMOTED_DEFERRED':
      return 'bg-warning/10 text-warning border border-warning/20';
    case 'PROMOTED_OVERRIDE':
      return 'bg-info/10 text-info border border-info/20';
    case 'SEMESTER_ADVANCED':
      return 'bg-success/10 text-success border border-success/20';
    default:
      return 'bg-theme-primary/10 text-theme-primary border border-theme-primary/20';
  }
};

/**
 * Returns a user-friendly label for promotion action outcomes.
 */
export const getPromotionActionLabel = (action: string): string => {
  switch (action) {
    case 'PROMOTED_OVERRIDE':
      return 'Overridden';
    case 'HELD_BACK':
      return 'Held Back';
    case 'PROMOTED_DEFERRED':
      return 'Promoted (Deferred)';
    case 'SEMESTER_ADVANCED':
      return 'Semester Advanced';
    default:
      return action.charAt(0) + action.slice(1).toLowerCase().replace('_', ' ');
  }
};

